import CommentIcon from "@mui/icons-material/Comment";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import documentoService from "../services/documento";
import protocoloService from "../services/protocolo";
import usuarioService from "../services/usuario";
import {
  GRUPO_ATIVIDADE_FLORESTAL,
  getAtividadeFlorestalResumo,
  PROTOCOLO_STATUS_AGUARDANDO_APROVACAO,
  PROTOCOLO_STATUS_EMITIDO,
  protocoloStatusLabels,
} from "../utils/protocoloConstants";
import { getMuiChipColor, getProtocoloPaletteKey } from "../utils/statusPalette";
import { formatCpfCnpj, formatDateToShow, formatPhone } from "../utils/formatters";
import { baixarRequerimentoProtocoloPdf } from "../utils/requerimentoProtocoloPdf";
import {
  abrirDocumentoArquivo,
  documentoEstaEnviado,
} from "../utils/abrirDocumentoArquivo";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import ComentariosDeUmDocumento from "./ComentariosDeUmDocumento";

function ProtocoloSecretarioModal({ open, onClose, protocolo, onAtualizado }) {
  const [detalhes, setDetalhes] = useState(null);
  const [requerente, setRequerente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [condicionantes, setCondicionantes] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [documentoSelecionado, setDocumentoSelecionado] = useState(null);
  const [comentarioDialogOpen, setComentarioDialogOpen] = useState(false);
  const [condicionanteSelecionado, setCondicionanteSelecionado] = useState(null);
  const [condicionanteComentarioDialogOpen, setCondicionanteComentarioDialogOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const protocoloId = protocolo?.id;

  const carregarDocumentos = useCallback(async () => {
    if (!protocoloId) return;
    try {
      const response = await documentoService.obterDocumentosPorProtocolo(protocoloId);
      setDocumentos(response?.data ?? response ?? []);
    } catch {
      setDocumentos([]);
    }
  }, [protocoloId]);

  const carregarCondicionantes = useCallback(async () => {
    if (!protocoloId) return;
    try {
      const response = await api.get(`Condicionante/protocolo/${protocoloId}`);
      setCondicionantes(response.data?.data ?? response.data ?? []);
    } catch {
      setCondicionantes([]);
    }
  }, [protocoloId]);

  const carregarDetalhes = useCallback(async () => {
    if (!protocoloId) return;
    setLoading(true);
    try {
      const data = await protocoloService.obterPorId(protocoloId);
      setDetalhes(data);
      setLoadingDocumentos(true);
      try {
        const usuario = await usuarioService.obterUsuarioPorId(data.createUserId);
        setRequerente(usuario);
      } catch {
        setRequerente(null);
      }
    } catch {
      setDetalhes(null);
    } finally {
      setLoading(false);
      setLoadingDocumentos(false);
    }
  }, [protocoloId]);

  useEffect(() => {
    if (!open || !protocoloId) {
      setDetalhes(null);
      setRequerente(null);
      setDocumentos([]);
      setCondicionantes([]);
      return;
    }
    carregarDetalhes();
    carregarDocumentos();
    carregarCondicionantes();
  }, [open, protocoloId, carregarDetalhes, carregarDocumentos, carregarCondicionantes]);

  const handleAprovar = async () => {
    if (!detalhes?.id) return;
    setSalvando(true);
    try {
      await protocoloService.aprovarSecretario(detalhes.id);
      alert("Protocolo aprovado com sucesso!");
      onAtualizado?.();
      onClose();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          error.response?.data?.title ||
          "Erro ao aprovar protocolo. Tente novamente.",
      );
    } finally {
      setSalvando(false);
    }
  };

  const nomeRequerente =
    requerente?.nome && requerente?.sobrenome
      ? `${requerente.nome} ${requerente.sobrenome}`.trim()
      : requerente?.username || "—";

  const podeAprovar = detalhes?.status === PROTOCOLO_STATUS_AGUARDANDO_APROVACAO;
  const emitido = detalhes?.status === PROTOCOLO_STATUS_EMITIDO;

  const handleBaixarRequerimento = async () => {
    if (!detalhes || !emitido) return;
    setDownloadingPdf(true);
    try {
      await baixarRequerimentoProtocoloPdf(detalhes, requerente);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Aprovação do Protocolo — {detalhes?.numeroProcesso || protocolo?.numeroProcesso || ""}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
          {loading && !detalhes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !detalhes ? (
            <Typography color="text.secondary">
              Não foi possível carregar os detalhes.
            </Typography>
          ) : (
            <>
              <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Informações do Processo
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Nº processo</Typography>
                    <Typography variant="body1">{detalhes.numeroProcesso || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Tipo de processo</Typography>
                    <Typography variant="body1">{detalhes.processoNome || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Grupo</Typography>
                    <Typography variant="body1">{detalhes.grupoDeAtividade || GRUPO_ATIVIDADE_FLORESTAL}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Atividade</Typography>
                    <Typography variant="body1">
                      {getAtividadeFlorestalResumo(detalhes.atividadeEmpreendimento)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Especificação</Typography>
                    <Typography variant="body1">{detalhes.especificacao || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip
                      label={protocoloStatusLabels[detalhes.status] ?? detalhes.status}
                      size="small"
                      color={getMuiChipColor(getProtocoloPaletteKey(detalhes.status))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Data</Typography>
                    <Typography variant="body1">{formatDateToShow(detalhes.createAt)}</Typography>
                  </Grid>
                </Grid>

                {detalhes.observacoesAnalista && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Observações do analista</Typography>
                    <Typography variant="body2">{detalhes.observacoesAnalista}</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Informações do Requerente
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Requerente:</strong> {nomeRequerente}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>CPF/CNPJ:</strong> {requerente?.cpf ? formatCpfCnpj(requerente.cpf) : "—"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Contato:</strong> {requerente?.telefone ? formatPhone(requerente.telefone) : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Endereço:</strong> {detalhes.empreendimentoEnderecoFormatado || "—"}
                </Typography>
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Documentos do Protocolo</Typography>
                {loadingDocumentos ? (
                  <CircularProgress size={24} />
                ) : documentos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum documento anexado.
                  </Typography>
                ) : (
                  <List dense>
                    {documentos.map((doc) => {
                      const docId = doc.data || doc.id;
                      const enviado = documentoEstaEnviado(doc.status);
                      return (
                        <ListItem
                          key={docId}
                          sx={{
                            borderLeft: "4px solid",
                            borderLeftColor: enviado ? "success.main" : "warning.main",
                            mb: 1,
                            borderRadius: 1,
                          }}
                        >
                          <ListItemIcon>
                            <FileIcon
                              role="img"
                              aria-label={`Documento, ${enviado ? "enviado" : "pendente"}`}
                              color={enviado ? "success" : "warning"}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.fileName}
                            secondary={doc.descricao || (enviado ? "Enviado" : "Pendente de envio")}
                          />
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            {enviado && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={async () => {
                                  try {
                                    await abrirDocumentoArquivo(docId);
                                  } catch (error) {
                                    alert(
                                      getApiErrorMessage(
                                        error,
                                        "Não foi possível abrir o documento.",
                                      ),
                                    );
                                  }
                                }}
                              >
                                Visualizar
                              </Button>
                            )}
                            {doc.hasMessages && (
                              <Tooltip title="Ver comentários">
                                <IconButton
                                  size="small"
                                  aria-label="Ver comentários"
                                  onClick={() => {
                                    setDocumentoSelecionado({ ...doc, id: docId, nome: doc.fileName });
                                    setComentarioDialogOpen(true);
                                  }}
                                >
                                  <Badge badgeContent="!" color="error">
                                    <CommentIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Condicionantes</Typography>
                {condicionantes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum condicionante cadastrado.
                  </Typography>
                ) : (
                  <List dense>
                    {condicionantes.map((cond) => (
                      <ListItem key={cond.id} sx={{ mb: 1, borderRadius: 1, bgcolor: "rgba(0,0,0,0.02)" }}>
                        <ListItemText
                          primary={cond.descricao}
                          secondary={cond.status === 1 ? "Pendente" : "Respondido"}
                        />
                        {cond.hasMessages && (
                          <Tooltip title="Ver comentários">
                            <IconButton
                              size="small"
                              aria-label="Ver comentários"
                              onClick={() => {
                                setCondicionanteSelecionado(cond);
                                setCondicionanteComentarioDialogOpen(true);
                              }}
                            >
                              <Badge badgeContent="!" color="error">
                                <CommentIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {emitido && (
            <Button
              onClick={handleBaixarRequerimento}
              disabled={downloadingPdf || loading}
              startIcon={<DescriptionIcon />}
            >
              {downloadingPdf ? "Baixando..." : "Baixar REQUERIMENTO"}
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          {podeAprovar && (
            <Button
              variant="contained"
              color="success"
              onClick={handleAprovar}
              disabled={salvando || loading}
            >
              {salvando ? "Aprovando..." : "Aprovar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ComentariosDeUmDocumento
        open={comentarioDialogOpen}
        onClose={() => setComentarioDialogOpen(false)}
        documento={documentoSelecionado}
      />

      <ComentariosDeUmDocumento
        open={condicionanteComentarioDialogOpen}
        onClose={() => setCondicionanteComentarioDialogOpen(false)}
        documento={condicionanteSelecionado}
        tipo="condicionante"
      />
    </>
  );
}

export default ProtocoloSecretarioModal;
